import { useEffect } from "react";

/**
 * Per-route <head> manager.
 *
 * Sets:
 *   - document.title
 *   - <meta name="robots"> (created on mount if missing)
 *   - <script type="application/ld+json"> (only when `jsonLd` provided;
 *     scrubbed from <head> on unmount so the next route doesn't inherit it)
 *
 * Why this lives client-side: index.html is a static template baked at
 * build time; SCW Contact Hub is a single-page React app served by a
 * non-SSR backend. Search/social crawlers that execute JS (Googlebot,
 * Twitter, Slack, WhatsApp Cloud API) read the rendered <head>. Static
 * crawlers fall back to the index.html defaults (public home metadata),
 * which is the right behaviour — public surfaces are the only ones we
 * want indexed.
 */
export function usePageMeta({ title, robots, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;

    if (robots) {
      let tag = document.querySelector('meta[name="robots"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "robots");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", robots);
    }

    let ldScript = null;
    if (jsonLd) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.text = JSON.stringify(jsonLd);
      ldScript.setAttribute("data-page-meta", "true");
      document.head.appendChild(ldScript);
    }

    return () => {
      if (ldScript && ldScript.parentNode) ldScript.parentNode.removeChild(ldScript);
    };
  }, [title, robots, jsonLd]);
}
